import { useScrollToBottom } from "#features/conversations/hooks/useScrollToBottom.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { type BackgroundColorValue, type BorderValue, Button, type ColorValue, useColorSchemeValue } from "@hopper-ui/components";
import { ArrowDownIcon } from "@hopper-ui/icons";
import classNames from "classnames";
import type { RefObject } from "react";
import "./ScrollToBottomButton.css";

interface Props {
    scrollableContainerRef: RefObject<HTMLDivElement | null>;
    // The page hides the button while it shows a question card in the composer's place.
    isHidden?: boolean;
}

export const ScrollToBottomButton = ({ scrollableContainerRef, isHidden = false }: Props) => {
    const { tShared } = useSharedTranslation();

    const { showButton, scrollToBottom } = useScrollToBottom(scrollableContainerRef);

    const color = useColorSchemeValue(
        "core_orchid-bloom-700",
        "neutral-weak"
    ) as ColorValue;

    const border = useColorSchemeValue(
        "core_orchid-bloom-75",
        "core_rock-500"
    ) as BorderValue;

    const borderHover = useColorSchemeValue(
        "core_orchid-bloom-100",
        "core_rock-500"
    ) as BorderValue;

    const backgroundColorHover = useColorSchemeValue(
        "core_orchid-bloom-50",
        "core_rock-800"
    ) as BackgroundColorValue;

    return (
        <Button
            position="absolute"
            bottom="10px"
            left="50%"
            variant="secondary"
            aria-label={tShared("scrollToBottom")}
            borderRadius="circle"
            zIndex={1}
            backgroundColorHover={backgroundColorHover}
            border={border}
            color={color}
            borderHover={borderHover}
            boxShadow="lifted"
            onPress={scrollToBottom}
            className={classNames("wai-scroll-to-bottom-button", { "wai-scroll-to-bottom-button-visible": showButton && !isHidden })}
        >
            <ArrowDownIcon size="sm" fill="currentcolor" />
        </Button>
    );
};
